import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Video as VideoIcon, Camera, Upload, Circle, Square, RotateCcw, Save, Trash2,
  Play, X, Film, Tag as TagIcon, Calendar, Pencil, ChevronLeft, ChevronRight,
  Sparkles, BookOpen,
} from 'lucide-react';
import { YearbookModal, GrowthAlbumModal } from './VideoAlbums';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Filter presets — CSS-only, feel like an old camcorder / photo album */
export const FILTERS = [
  { key: 'none',     name: 'Idag',        css: 'none', swatch: 'linear-gradient(135deg,#FFF,#EEE)' },
  { key: 'vintage',  name: 'VHS',         css: 'sepia(0.4) contrast(1.1) saturate(0.85) brightness(0.97)', swatch: 'linear-gradient(135deg,#E8D5AA,#B89968)' },
  { key: 'warm',     name: 'Super 8',     css: 'contrast(1.12) saturate(1.35) hue-rotate(-8deg) brightness(1.05)', swatch: 'linear-gradient(135deg,#FFD19A,#FF8F6B)' },
  { key: 'bw',       name: 'Svartvit',    css: 'grayscale(1) contrast(1.2)', swatch: 'linear-gradient(135deg,#DDD,#333)' },
  { key: 'dream',    name: 'Dröm',        css: 'blur(0.6px) saturate(1.4) brightness(1.08) contrast(0.95)', swatch: 'linear-gradient(135deg,#FFE8F5,#C8B6FF)' },
  { key: 'polaroid', name: 'Polaroid',    css: 'sepia(0.22) contrast(1.22) saturate(1.35) brightness(1.06)', swatch: 'linear-gradient(135deg,#FFF0D5,#E8B97D)' },
  { key: 'film',     name: 'Film',        css: 'sepia(0.32) contrast(1.18) saturate(0.88) hue-rotate(-12deg)', swatch: 'linear-gradient(135deg,#E4D4B0,#8FA07E)' },
  { key: 'neon',     name: 'Neon 90-tal', css: 'saturate(1.8) contrast(1.25) hue-rotate(12deg)', swatch: 'linear-gradient(135deg,#FF6EC7,#3DFFEA)' },
];

const filterByKey = (k) => FILTERS.find((f) => f.key === k) || FILTERS[0];

const MONTH_SV = [
  'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
  'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'
];

function formatStamp(d) {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}.${mo}.${da}  ${hh}:${mm}`;
}

function FilterPicker({ value, onChange, size = 'md' }) {
  const height = size === 'sm' ? 'h-10' : 'h-12';
  return (
    <div className="flex gap-2 overflow-x-auto pb-1" data-testid="video-filter-picker">
      {FILTERS.map((f) => (
        <button
          key={f.key}
          onClick={() => onChange(f.key)}
          className={`shrink-0 rounded-2xl border-[3px] border-black p-1.5 transition ${
            value === f.key ? 'shadow-[3px_3px_0_#000] translate-y-[-2px]' : 'shadow-[2px_2px_0_#000]'
          } bg-white`}
          data-testid={`video-filter-${f.key}`}
        >
          <div
            className={`${height} w-14 rounded-xl border-2 border-black`}
            style={{ background: f.swatch }}
          />
          <div className={`text-[10px] font-bold mt-1 ${value === f.key ? '' : 'opacity-70'}`}>
            {f.name}
          </div>
        </button>
      ))}
    </div>
  );
}

function CamcorderOverlay({ recording, elapsed }) {
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(Math.floor(elapsed % 60)).padStart(2, '0');
  return (
    <>
      <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/55 text-white text-[11px] font-mono px-2 py-1 rounded pointer-events-none">
        {recording && <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
        <span>{recording ? 'REC' : 'PAUS'}</span>
        <span className="opacity-80">{mm}:{ss}</span>
      </div>
      <div className="absolute bottom-2 right-2 bg-black/55 text-white text-[10px] font-mono px-2 py-1 rounded pointer-events-none">
        {formatStamp(new Date())}
      </div>
    </>
  );
}

const MAX_RECORD_SECONDS = 60;

function Recorder({ onSave, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [stage, setStage] = useState('idle'); // idle | recording | preview
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [filter, setFilter] = useState('polaroid');
  const [caption, setCaption] = useState('');
  const [tagStr, setTagStr] = useState('');
  const [memoryDate, setMemoryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('user');

  const startCamera = useCallback(async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setError(null);
    } catch (e) {
      console.error(e);
      setError('Kan inte öppna kameran. Tillåt kamera & mikrofon i webbläsaren.');
    }
  }, [facingMode]);

  useEffect(() => {
    if (stage === 'idle') startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (timerRef.current) clearInterval(timerRef.current);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [facingMode]);

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ? 'video/webm;codecs=vp8,opus' : 'video/webm');
    const rec = new MediaRecorder(streamRef.current, { mimeType: mime });
    rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const b = new Blob(chunksRef.current, { type: 'video/webm' });
      setBlob(b);
      const url = URL.createObjectURL(b);
      setPreviewUrl(url);
      setStage('preview');
      // Stop camera once recording saved
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
    rec.start(250);
    recorderRef.current = rec;
    setRecording(true);
    setStage('recording');
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed((s) => {
        const next = s + 1;
        if (next >= MAX_RECORD_SECONDS) {
          stopRecording();
        }
        return next;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  };

  const retake = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setBlob(null);
    setElapsed(0);
    setStage('idle');
    startCamera();
  };

  const saveClip = async () => {
    if (!blob) return;
    setSaving(true);
    try {
      const form = new FormData();
      form.append('file', blob, `clip_${Date.now()}.webm`);
      form.append('caption', caption);
      form.append('filter_name', filter);
      form.append('memory_date', memoryDate);
      form.append('tags', tagStr);
      form.append('duration_seconds', String(elapsed));
      await axios.post(`${API}/videos`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success('Sparat i albumet 🎞️');
      onSave?.();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const filterCss = filterByKey(filter).css;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm p-3 flex items-center justify-center overflow-y-auto"
      data-testid="video-recorder-modal"
      onClick={onClose}
    >
      <div
        className="neo-card bg-white w-full max-w-xl p-4 my-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center gap-2">
            <Camera className="w-5 h-5" /> {stage === 'preview' ? 'Förhandsgranska' : 'Spela in minne'}
          </h3>
          <button
            onClick={onClose}
            className="neo-btn p-1.5 bg-white"
            aria-label="Stäng"
            data-testid="video-recorder-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="neo-card p-3 bg-red-50 text-red-700 text-sm mb-3">
            {error}
          </div>
        )}

        {/* Video frame */}
        <div className="relative rounded-2xl border-[3px] border-black overflow-hidden bg-black" style={{ aspectRatio: '4 / 3' }}>
          {stage === 'preview' && previewUrl ? (
            <video
              src={previewUrl}
              controls
              playsInline
              className="w-full h-full object-contain"
              style={{ filter: filterCss }}
              data-testid="video-recorder-preview"
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ filter: filterCss, transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
              data-testid="video-recorder-camera"
            />
          )}
          {stage !== 'preview' && <CamcorderOverlay recording={recording} elapsed={elapsed} />}
        </div>

        {/* Controls */}
        {stage !== 'preview' ? (
          <div className="mt-3 space-y-3">
            <FilterPicker value={filter} onChange={setFilter} />
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setFacingMode((m) => (m === 'user' ? 'environment' : 'user'))}
                className="neo-btn px-3 py-2 bg-white flex items-center gap-1 text-sm"
                title="Byt kamera"
                data-testid="video-recorder-flip"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              {!recording ? (
                <button
                  onClick={startRecording}
                  className="neo-btn px-5 py-2 bg-[#FF6B6B] text-white font-bold flex items-center gap-2"
                  data-testid="video-recorder-start"
                >
                  <Circle className="w-5 h-5 fill-white" /> Börja spela in
                </button>
              ) : (
                <button
                  onClick={stopRecording}
                  className="neo-btn px-5 py-2 bg-black text-white font-bold flex items-center gap-2"
                  data-testid="video-recorder-stop"
                >
                  <Square className="w-4 h-4 fill-white" /> Stoppa ({MAX_RECORD_SECONDS - elapsed}s kvar)
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            <FilterPicker value={filter} onChange={setFilter} size="sm" />
            <div>
              <label className="text-xs font-bold block mb-1">Bildtext (valfritt)</label>
              <input
                type="text"
                placeholder="T.ex. Tilde och Maja klär ut sig"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="neo-input w-full"
                maxLength={280}
                data-testid="video-recorder-caption"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold block mb-1">Minnesdatum</label>
                <input
                  type="date"
                  value={memoryDate}
                  onChange={(e) => setMemoryDate(e.target.value)}
                  className="neo-input w-full"
                  data-testid="video-recorder-date"
                />
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Taggar (kommaseparerade)</label>
                <input
                  type="text"
                  placeholder="Tilde, Maja"
                  value={tagStr}
                  onChange={(e) => setTagStr(e.target.value)}
                  className="neo-input w-full"
                  data-testid="video-recorder-tags"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={retake}
                className="neo-btn flex-1 py-2 bg-white font-bold flex items-center justify-center gap-2"
                data-testid="video-recorder-retake"
              >
                <RotateCcw className="w-4 h-4" /> Gör om
              </button>
              <button
                onClick={saveClip}
                disabled={saving}
                className="neo-btn flex-1 py-2 bg-[#B9FBC0] font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="video-recorder-save"
              >
                <Save className="w-4 h-4" /> {saving ? 'Sparar…' : 'Spara i album'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UploadModal({ onSave, onClose }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [filter, setFilter] = useState('polaroid');
  const [caption, setCaption] = useState('');
  const [tagStr, setTagStr] = useState('');
  const [memoryDate, setMemoryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('video/')) { toast.error('Välj en videofil'); return; }
    if (f.size > 60 * 1024 * 1024) { toast.error('Max 60 MB'); return; }
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const save = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('caption', caption);
      form.append('filter_name', filter);
      form.append('memory_date', memoryDate);
      form.append('tags', tagStr);
      await axios.post(`${API}/videos`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      toast.success('Sparat 🎞️');
      onSave?.();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm p-3 flex items-center justify-center overflow-y-auto"
      onClick={onClose}
      data-testid="video-upload-modal"
    >
      <div className="neo-card bg-white w-full max-w-xl p-4 my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center gap-2"><Upload className="w-5 h-5" /> Ladda upp video</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" aria-label="Stäng" data-testid="video-upload-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {!previewUrl ? (
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full py-10 border-[3px] border-dashed border-black rounded-2xl bg-[#FFF7E0] hover:bg-[#FFE8A3] flex flex-col items-center gap-2 font-bold"
            data-testid="video-upload-pick"
          >
            <Film className="w-8 h-8" />
            <span>Välj video från galleri</span>
            <span className="text-xs font-normal text-black/60">(max 60 MB)</span>
          </button>
        ) : (
          <div className="rounded-2xl border-[3px] border-black overflow-hidden bg-black" style={{ aspectRatio: '4 / 3' }}>
            <video
              src={previewUrl}
              controls
              playsInline
              className="w-full h-full object-contain"
              style={{ filter: filterByKey(filter).css }}
              data-testid="video-upload-preview"
            />
          </div>
        )}
        <input ref={inputRef} type="file" accept="video/*" onChange={pick} className="hidden" data-testid="video-upload-input" />
        {previewUrl && (
          <div className="mt-3 space-y-3">
            <FilterPicker value={filter} onChange={setFilter} size="sm" />
            <input
              type="text"
              placeholder="Bildtext (valfritt)"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="neo-input w-full"
              maxLength={280}
              data-testid="video-upload-caption"
            />
            <div className="grid grid-cols-2 gap-2">
              <input type="date" value={memoryDate} onChange={(e) => setMemoryDate(e.target.value)} className="neo-input w-full" data-testid="video-upload-date" />
              <input type="text" placeholder="Taggar" value={tagStr} onChange={(e) => setTagStr(e.target.value)} className="neo-input w-full" data-testid="video-upload-tags" />
            </div>
            <button
              onClick={save}
              disabled={saving}
              className="neo-btn w-full py-2 bg-[#B9FBC0] font-bold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="video-upload-save"
            >
              <Save className="w-4 h-4" /> {saving ? 'Sparar…' : 'Spara i album'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PlayerModal({ clip, onClose, onDeleted, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [caption, setCaption] = useState(clip.caption || '');
  const [filter, setFilter] = useState(clip.filter || 'none');
  const [tagStr, setTagStr] = useState((clip.tags || []).join(', '));
  const [memoryDate, setMemoryDate] = useState(clip.memory_date?.slice(0, 10) || '');
  const [saving, setSaving] = useState(false);

  const streamSrc = `${API}/videos/${clip.id}/stream`;

  const del = async () => {
    if (!window.confirm('Ta bort detta minne? Det går inte att ångra.')) return;
    try {
      await axios.delete(`${API}/videos/${clip.id}`, { withCredentials: true });
      toast.success('Borttaget');
      onDeleted?.();
      onClose();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const tags = tagStr.split(',').map((t) => t.trim()).filter(Boolean);
      await axios.patch(
        `${API}/videos/${clip.id}`,
        { caption, filter, tags, memory_date: memoryDate },
        { withCredentials: true }
      );
      toast.success('Uppdaterat');
      setEditing(false);
      onUpdated?.();
    } catch {
      toast.error('Kunde inte uppdatera');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm p-3 flex items-center justify-center overflow-y-auto"
      onClick={onClose}
      data-testid="video-player-modal"
    >
      <div className="w-full max-w-2xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="rounded-2xl border-[3px] border-black overflow-hidden bg-black" style={{ aspectRatio: '4 / 3' }}>
          <video
            src={streamSrc}
            controls
            autoPlay
            playsInline
            className="w-full h-full object-contain"
            style={{ filter: filterByKey(filter).css }}
            data-testid="video-player-video"
          />
        </div>
        <div className="neo-card mt-3 p-4 bg-white">
          {editing ? (
            <div className="space-y-3">
              <FilterPicker value={filter} onChange={setFilter} size="sm" />
              <input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Bildtext"
                className="neo-input w-full"
                data-testid="video-player-caption-input"
              />
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={memoryDate} onChange={(e) => setMemoryDate(e.target.value)} className="neo-input w-full" />
                <input type="text" value={tagStr} onChange={(e) => setTagStr(e.target.value)} placeholder="Taggar" className="neo-input w-full" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} className="neo-btn flex-1 py-2 bg-white font-bold">Avbryt</button>
                <button onClick={save} disabled={saving} className="neo-btn flex-1 py-2 bg-[#B9FBC0] font-bold disabled:opacity-60" data-testid="video-player-save">
                  {saving ? 'Sparar…' : 'Spara'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  {clip.caption ? (
                    <p className="font-bold text-sm" data-testid="video-player-caption">{clip.caption}</p>
                  ) : (
                    <p className="font-bold text-sm text-black/40 italic">Utan bildtext</p>
                  )}
                  <p className="text-xs text-black/60 mt-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(clip.memory_date).toLocaleDateString('sv-SE')}
                    <span className="mx-1">·</span>
                    <Film className="w-3 h-3" /> {filterByKey(clip.filter).name}
                  </p>
                  {(clip.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {clip.tags.map((t) => (
                        <span key={t} className="text-[10px] font-bold px-2 py-0.5 rounded-full border-[2px] border-black bg-[#FDF0D5]">
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(true)} className="neo-btn flex-1 py-2 bg-white font-bold flex items-center justify-center gap-1" data-testid="video-player-edit">
                  <Pencil className="w-4 h-4" /> Ändra
                </button>
                <button onClick={del} className="neo-btn flex-1 py-2 bg-white text-red-600 font-bold flex items-center justify-center gap-1" data-testid="video-player-delete">
                  <Trash2 className="w-4 h-4" /> Ta bort
                </button>
                <button onClick={onClose} className="neo-btn flex-1 py-2 bg-[#FFC8DD] font-bold">Stäng</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ClipCard({ clip, onOpen }) {
  const streamSrc = `${API}/videos/${clip.id}/stream`;
  const d = new Date(clip.memory_date);
  return (
    <button
      onClick={() => onOpen(clip)}
      className="neo-card p-3 bg-white text-left hover:translate-y-[-1px] transition"
      data-testid="video-clip-card"
      style={{
        background: 'linear-gradient(180deg,#FFFEF8,#F5ECDB)',
      }}
    >
      <div className="relative rounded-xl border-[3px] border-black overflow-hidden bg-black" style={{ aspectRatio: '4 / 3' }}>
        <video
          src={streamSrc}
          preload="metadata"
          muted
          playsInline
          className="w-full h-full object-cover pointer-events-none"
          style={{ filter: filterByKey(clip.filter).css }}
        />
        <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/90 border-[3px] border-black flex items-center justify-center shadow-[2px_2px_0_#000]">
            <Play className="w-5 h-5 ml-0.5" />
          </div>
        </div>
        <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">
          {formatStamp(d).replace('  ', ' ')}
        </div>
      </div>
      <div className="mt-2">
        <p className="text-sm font-bold leading-tight line-clamp-2">
          {clip.caption || <span className="text-black/40 italic">Utan bildtext</span>}
        </p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full border-2 border-black bg-white">
            {filterByKey(clip.filter).name}
          </span>
          {(clip.tags || []).length > 0 && (
            <span className="text-[10px] text-black/60 truncate ml-1">#{clip.tags.join(' #')}</span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function PrivateVideos() {
  const [clips, setClips] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeClip, setActiveClip] = useState(null);
  const [tagFilter, setTagFilter] = useState('');
  const [monthOffset, setMonthOffset] = useState(0); // 0 = all months
  const [yearbookOpen, setYearbookOpen] = useState(false);
  const [growthTag, setGrowthTag] = useState(null);

  const fetchClips = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/videos`, { withCredentials: true });
      setClips(data.videos || []);
      setAllTags(data.tags || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClips(); }, [fetchClips]);

  const visibleClips = useMemo(() => {
    if (!tagFilter) return clips;
    return clips.filter((c) => (c.tags || []).includes(tagFilter));
  }, [clips, tagFilter]);

  const grouped = useMemo(() => {
    const map = new Map();
    visibleClips.forEach((c) => {
      const d = new Date(c.memory_date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [visibleClips]);

  const canRecord = typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia;

  return (
    <div className="space-y-4" data-testid="private-videos">
      {/* Header */}
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFF2D6] via-[#FFE8F0] to-[#E8D5FF] relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="font-bold text-xl flex items-center gap-2">
            <VideoIcon className="w-5 h-5" /> Minnen
          </h2>
          <p className="text-xs text-black/70 mt-1">
            Ert privata videoalbum. Små stunder i vardagen, tjejernas uppväxt — bara för er. 🎞️✨
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            {canRecord && (
              <button
                onClick={() => setRecording(true)}
                className="neo-btn px-3 py-2 bg-[#FF6B6B] text-white font-bold flex items-center gap-2 text-sm"
                data-testid="private-videos-record-btn"
              >
                <Camera className="w-4 h-4" /> Spela in
              </button>
            )}
            <button
              onClick={() => setUploading(true)}
              className="neo-btn px-3 py-2 bg-white font-bold flex items-center gap-2 text-sm"
              data-testid="private-videos-upload-btn"
            >
              <Upload className="w-4 h-4" /> Ladda upp
            </button>
            {clips.length > 0 && (
              <button
                onClick={() => setYearbookOpen(true)}
                className="neo-btn px-3 py-2 bg-[#FFE8A3] font-bold flex items-center gap-2 text-sm"
                data-testid="private-videos-yearbook-btn"
                title="Spela upp alla klipp från ett år som en årsbok"
              >
                <Sparkles className="w-4 h-4" /> Årsbok
              </button>
            )}
          </div>
        </div>
        {/* Decorative VHS-tape vibe */}
        <div className="absolute -right-6 -bottom-6 opacity-10 text-[120px] leading-none">🎞️</div>
      </header>

      {/* Tag filter */}
      {allTags.length > 0 && (
        <div className="space-y-1.5" data-testid="private-videos-tag-bar">
          <p className="text-[10px] font-bold opacity-60 px-1">Filtrera eller öppna tillväxtalbum:</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setTagFilter('')}
              className={`neo-btn px-3 py-1 text-xs font-bold shrink-0 ${!tagFilter ? 'bg-[#FFC8DD]' : 'bg-white'}`}
            >
              Alla
            </button>
            {allTags.map((t) => (
              <div key={t} className="flex shrink-0 rounded-full border-[2px] border-black overflow-hidden shadow-[2px_2px_0_#000]">
                <button
                  onClick={() => setTagFilter(t === tagFilter ? '' : t)}
                  className={`px-3 py-1 text-xs font-bold flex items-center gap-1 ${tagFilter === t ? 'bg-[#C8B6FF]' : 'bg-white'}`}
                  data-testid={`private-videos-tag-${t}`}
                >
                  <TagIcon className="w-3 h-3" /> {t}
                </button>
                <button
                  onClick={() => setGrowthTag(t)}
                  className="px-2 py-1 text-xs bg-[#FDF0D5] border-l-[2px] border-black hover:bg-[#FFE8A3]"
                  title={`Öppna tillväxtalbum för ${t}`}
                  data-testid={`private-videos-growth-${t}`}
                  aria-label={`Öppna tillväxtalbum för ${t}`}
                >
                  <BookOpen className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {loading ? (
        <div className="neo-card p-6 text-center text-sm text-black/60">Laddar…</div>
      ) : visibleClips.length === 0 ? (
        <div className="neo-card p-8 text-center bg-white">
          <div className="text-5xl mb-2">🎞️</div>
          <h3 className="font-bold">{tagFilter ? `Inga minnen med taggen "${tagFilter}"` : 'Tomt album ännu'}</h3>
          <p className="text-sm text-black/70 mt-1">
            {tagFilter ? 'Klicka "Alla" ovan för att se alla dina minnen.' : 'Spela in ditt första minne ovan — en sekund räcker. Bara ni två ser det.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([key, items]) => {
            const [y, m] = key.split('-');
            const mLabel = MONTH_SV[parseInt(m, 10) - 1];
            return (
              <section key={key}>
                <h3 className="font-bold text-sm text-black/70 mb-2 flex items-center gap-2 sticky top-0 bg-gradient-to-r from-[#FFF7E0]/80 to-transparent px-1">
                  {mLabel} {y} · <span className="text-black/50">{items.length}</span>
                </h3>
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((c) => <ClipCard key={c.id} clip={c} onOpen={setActiveClip} />)}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Footer privacy reassurance */}
      <p className="text-[11px] text-center text-black/50 italic">
        🔒 Allt i "Minnen" är helt privat. Bara du med ditt lösenord ser videon.
      </p>

      {recording && <Recorder onSave={fetchClips} onClose={() => setRecording(false)} />}
      {uploading && <UploadModal onSave={fetchClips} onClose={() => setUploading(false)} />}
      {activeClip && (
        <PlayerModal
          clip={activeClip}
          onClose={() => setActiveClip(null)}
          onDeleted={fetchClips}
          onUpdated={fetchClips}
        />
      )}
      {yearbookOpen && (
        <YearbookModal clips={clips} onClose={() => setYearbookOpen(false)} />
      )}
      {growthTag && (
        <GrowthAlbumModal
          clips={clips}
          tag={growthTag}
          onClose={() => setGrowthTag(null)}
          onOpenClip={(c) => { setGrowthTag(null); setActiveClip(c); }}
        />
      )}
    </div>
  );
}
